import {
  ActionButton,
  Button,
  DialogContainer,
  Flex,
  Item,
  Link as V3Link,
  Picker,
  SearchField,
  Text,
  TextField,
  useAsyncList,
  View,
} from "@adobe/react-spectrum";
import AddCircle from "@spectrum-icons/workflow/AddCircle";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { error } from "@react/react-spectrum/Toast";
import { eamApi } from "../../../../../api/eamApi";
import CamSlidingPane from "../../../../../components/Common/CamSlidingPane/CamSlidingPane";
import Table from "../../../../../components/Common/Table";
import CreateAccountDialog from "../../../../../components/Dialog/CreateAccountDialog";
import AccountMappingForm from "../../../../../components/Forms/AccountMappingForm/AccountMappingForm";
import { EAMSearchSourceAccountColumns } from "../../../../../constants/Columns";
import { SourceSystemAccountsLabel } from "../../../../../constants/SlidingPaneLabels";

/**
 * Maps OrphanLookupResponse and similar shapes to table rows.
 * Success: { message, source_system_account: { account_id, source_system, ... } }
 */
function normalizeOrphanLookupResults(data) {
  if (data == null) return [];
  if (
    data?.source_system_account &&
    typeof data.source_system_account === "object"
  ) {
    const acc = data.source_system_account;
    return [
      acc.id != null
        ? acc
        : {
            ...acc,
            id: `${acc.account_id ?? ""}-${acc.source_system ?? ""}`,
          },
    ];
  }
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (data?.orphanAccount && typeof data.orphanAccount === "object")
    return [data.orphanAccount];
  if (data?.account && typeof data.account === "object") return [data.account];
  if (typeof data === "object" && (data?.account_id ?? data?.id)) return [data];
  return [];
}

export default function Step2AddAccount({
  activeStep,
  subscriberSourceSystems,
  modifiedSyncSet,
  originalSourceAccount,
  onAddAccount,
  onBack,
  authState,
}) {
  const [typeOfDialog, setTypeOfDialog] = useState();
  const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);
  const [sidePaneData, setSidePaneData] = useState([]);
  const [sidePaneTitle, setSidePaneTitle] = useState("");
  const [sidePaneLabels, setSidePaneLabels] = useState([]);
  const [selectedSourceSystem, setSelectedSourceSystem] = useState("");
  const [accountIdSearchText, setAccountIdSearchText] = useState("");

  const searchSourceAccountColumns = EAMSearchSourceAccountColumns();

  const missingSourceSystems = useMemo(
    () =>
      (subscriberSourceSystems || []).filter(
        (systemName) =>
          !modifiedSyncSet?.some((acc) => acc?.source_system === systemName)
      ),
    [subscriberSourceSystems, modifiedSyncSet]
  );

  useEffect(() => {
    if (
      missingSourceSystems.length > 0 &&
      !missingSourceSystems.includes(selectedSourceSystem)
    ) {
      setSelectedSourceSystem(missingSourceSystems[0]);
    }
  }, [missingSourceSystems]);

  const list = useAsyncList({
    initialFilterText: "",
    async load({ filterText }) {
      if (!filterText?.trim() || !selectedSourceSystem) {
        return { items: [] };
      }
      const result = await eamApi.orphanLookup(
        filterText.trim(),
        selectedSourceSystem,
        authState.accessToken
      );
      if (
        result != null &&
        typeof result === "object" &&
        result.errorCode != null
      ) {
        const msg =
          typeof result.message === "string" ? result.message.trim() : "";
        if (msg) error(msg, { timeout: 5000 });
        return { items: [] };
      }
      const items = normalizeOrphanLookupResults(result);
      const accountMissing =
        result == null ||
        typeof result !== "object" ||
        result.source_system_account == null;
      const msg =
        typeof result?.message === "string" ? result.message.trim() : "";
      if (accountMissing && items.length === 0 && msg) {
        error(msg, { timeout: 5000 });
      }
      return { items };
    },
    getKey: (item) =>
      item?.id ?? `${item?.account_id ?? ""}-${item?.source_system ?? ""}`,
  });

  const setSidePaneDetails = (data, title, labels) => {
    setSidePaneLabels(labels);
    setSidePaneData(data);
    setSidePaneTitle(title);
    setIsSidePaneOpen(!isSidePaneOpen);
  };

  const handleAddAccount = (account) => {
    onAddAccount(account, { fromExistingLookup: true });
  };

  const handleCreateAccountSuccess = (updaterOrAccount) => {
    if (typeof updaterOrAccount === "function") {
      const createdAccount = updaterOrAccount(undefined);
      onAddAccount(createdAccount, { fromExistingLookup: false });
      onBack();
    } else if (updaterOrAccount) {
      onAddAccount(updaterOrAccount, { fromExistingLookup: false });
      onBack();
    }
  };

  const renderCell = (colKey, row) => {
    const alreadyInSyncSet = modifiedSyncSet?.some(
      (acc) =>
        acc?.source_system === row?.source_system &&
        acc?.account_id === row?.account_id
    );

    if (colKey === "actions") {
      return (
        <ActionButton
          isQuiet
          isDisabled={alreadyInSyncSet}
          onPress={() => handleAddAccount(row)}
        >
          <View>
            <AddCircle color={alreadyInSyncSet ? "" : "positive"} />
          </View>
        </ActionButton>
      );
    }
    if (colKey === "account_id") {
      return (
        <V3Link
          isQuiet
          onPress={() => {
            setSidePaneDetails(
              row,
              row.account_name,
              SourceSystemAccountsLabel()
            );
          }}
        >
          {row[colKey]}
        </V3Link>
      );
    }
    if (colKey === "address_key") {
      const address = row?.address?.[0]
        ? `${row.address[0].street || ""} ${row.address[0].street_sup || ""} ${
            row.address[0].city || ""
          }, ${row.address[0].state || ""} ${row.address[0].country || ""} ${
            row.address[0].postalcode || ""
          }`.trim()
        : "";
      return <Text>{address}</Text>;
    }
    return <Text>{row[colKey]}</Text>;
  };

  if (activeStep !== 2) return null;

  if (missingSourceSystems.length === 0) {
    return (
      <View
        UNSAFE_className={`card ${activeStep === 2 ? "active" : "hide"}`}
        key="step2-add"
      >
        <Flex direction="column" gap="size-200" marginTop="size-300">
          <Text>
            No missing source systems. Sync set is complete for all subscriber
            systems.
          </Text>
          {/* <Button variant="primary" onPress={onBack}>
            Back to sync set
          </Button> */}
        </Flex>
      </View>
    );
  }

  const effectiveSourceSystem = selectedSourceSystem || missingSourceSystems[0];

  return (
    <View
      UNSAFE_className={`card ${activeStep === 2 ? "active" : "hide"}`}
      key="step2-add"
      overflow="auto"
    >
      <Flex marginTop="size-300" gap="size-125" direction="column">
        <Flex wrap gap="size-150" justifyContent="space-between">
          <Flex wrap gap="size-150">
            <Picker
              label="Source system"
              selectedKey={effectiveSourceSystem}
              onSelectionChange={(key) => {
                setSelectedSourceSystem(key);
                list.setFilterText("");
                list.reload();
              }}
            >
              {missingSourceSystems.map((name) => (
                <Item key={name}>{name}</Item>
              ))}
            </Picker>
            <SearchField
              label="CRM Account Id"
              value={accountIdSearchText}
              onChange={setAccountIdSearchText}
              onSubmit={() => list.setFilterText(accountIdSearchText)}
            />
            <TextField
              label="Source system"
              value={effectiveSourceSystem}
              isReadOnly
            />
          </Flex>
          <Flex alignItems="end">
            <Button
              variant="accent"
              onPress={() => setTypeOfDialog("createAccount")}
            >
              Create new account
            </Button>
          </Flex>
        </Flex>
        <Flex
          direction="row"
          gap="size-125"
          height="size-3600"
          marginTop="size-300"
          UNSAFE_style={{
            backgroundColor: "white",
            borderRadius: "0.5rem",
          }}
          UNSAFE_className="stepper-box"
        >
          <Table
            columns={searchSourceAccountColumns}
            rows={list.items}
            renderCell={renderCell}
            loadingState={list.loadingState}
            density="compact"
          />
        </Flex>
        {/* <Button variant="primary" onPress={onBack}>
          Back to sync set
        </Button> */}
      </Flex>

      <DialogContainer onDismiss={() => setTypeOfDialog("")}>
        {typeOfDialog === "createAccount" && (
          <CreateAccountDialog
            sourceSystem={effectiveSourceSystem}
            setToAccount={handleCreateAccountSuccess}
            originalSourceAccount={originalSourceAccount}
          />
        )}
      </DialogContainer>

      {isSidePaneOpen && (
        <CamSlidingPane
          isPaneOpenFlag={isSidePaneOpen}
          handleOpenDialog={() => setIsSidePaneOpen(false)}
          paneTitle={sidePaneTitle}
          Component={
            <AccountMappingForm data={sidePaneData} labels={sidePaneLabels} />
          }
        />
      )}
    </View>
  );
}

Step2AddAccount.propTypes = {
  activeStep: PropTypes.number.isRequired,
  subscriberSourceSystems: PropTypes.arrayOf(PropTypes.string),
  modifiedSyncSet: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      source_system: PropTypes.string,
      account_id: PropTypes.string,
    })
  ),
  originalSourceAccount: PropTypes.shape({
    account_id: PropTypes.string,
    source_system: PropTypes.string,
    account_name: PropTypes.string,
    address: PropTypes.arrayOf(
      PropTypes.shape({
        street: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        country: PropTypes.string,
        postalcode: PropTypes.string,
      })
    ),
  }),
  onAddAccount: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  authState: PropTypes.shape({
    accessToken: PropTypes.string,
  }).isRequired,
};

Step2AddAccount.defaultProps = {
  subscriberSourceSystems: [],
  modifiedSyncSet: [],
  originalSourceAccount: undefined,
};
